import React, { useState, useEffect } from 'react';
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Link,
  Box,
} from '@mui/material';
import { BoxFullPage } from 'app/components/shared/StyledFullPage';

import {
  useGetAppConfigQuery,
  useGetAvailabilityQuery,
  useGetFlirtAccountSetupQuery,
  useGetUserQuery,
  useUpdateFlirtAccountSetupMutation,
} from 'app/api/mainApi';

import AddPhoneStep from 'app/components/advisor_signup/AddPhoneStep';
import IdVerificationStep from 'app/components/advisor_signup/IdVerificationStep';
import PayoutInfoStep from 'app/components/advisor_signup/PayoutInfoStep';
import AddCreditCardStep from 'app/components/advisor_signup/AddCreditCardStep';

const stepsData = [
  { key: 'add_phone', label: 'Add Phone Number', shortLabel: 'Phone' },
  { key: 'add_cc', label: 'Add a credit card', shortLabel: 'Card' },
  { key: 'verify_id', label: 'ID Verification', shortLabel: 'ID' },
  { key: 'add_payout_info', label: 'Payout Info', shortLabel: 'Payout' },
  { key: 'gender', label: 'Set Gender', shortLabel: 'Gender' },
];

const FlirtAccountSetupContainer = () => {
  const appConfig = useGetAppConfigQuery().data;
  const currentUser = appConfig?.current_user;
  const { data: availability } = useGetAvailabilityQuery();
  const phones = availability?.phones;
  const { data: flirtAccountSetup } = useGetFlirtAccountSetupQuery();
  const [currentStep, setCurrentStep] = useState('');
  const [inStep, setInStep] = useState(false);
  const activeStepIndex = stepsData.findIndex(s => s.key === currentStep);
  const [updateFlirtAccountSetup] = useUpdateFlirtAccountSetupMutation();
  const {
    data: {
      user: {
        idv_status: idvStatus,
        idv_date: idvDate,
      } = {},
    } = {},
  } = useGetUserQuery();

  useEffect(() => {
    if (flirtAccountSetup?.step) {
      setCurrentStep(flirtAccountSetup.step);
      setInStep(false);
    }
  }, [flirtAccountSetup]);

  useEffect(() => {
    if (idvStatus === 'verified' && currentStep === 'verify_id') {
      updateFlirtAccountSetup();
    }
  }, [idvStatus]);

  const handleStartStep = () => setInStep(true);

  const handleCancelStep = () => setInStep(false);

  const inIdVerificationProcess = () => {
    return idvStatus !== 'not_required' &&
    idvStatus !== 'completed' &&
    currentStep === 'verify_id';
  };

  const ableToStartStep = () => {
    if (currentStep === 'verify_id') {
      return idvStatus === 'not_required';
    }

    return true;
  };

  const renderStepper = () => {
    const shortLabels = ['Phone', 'Card', 'ID', 'Payout'];
    const labelsToShow = inStep ? shortLabels : stepsData.map(s => s.label);
    const stepperSx = {
      marginBottom: inStep ? 2 : 4,
      '& .MuiStepIcon-root': {
        width: inStep ? 30 : 50,
        height: inStep ? 30 : 50,
      },
      '& .MuiStepIcon-text': {
        fontSize: inStep ? '0.75rem' : '1rem',
      },
      '& .MuiStepConnector-root': {
        top: inStep ? 'calc(15px)' : '24px',
      },
      '& .MuiStepConnector-line': {
        marginLeft: inStep ? '15px' : '25px',
        marginRight: inStep ? '15px' : '25px',
      },
    };
    return (
      <Container>
        <Stepper
          activeStep={activeStepIndex}
          alternativeLabel
          sx={stepperSx}
        >
          {stepsData.map((step, index) => (
            <Step key={step.key}>
              <StepLabel>
                {labelsToShow[index]}
                {activeStepIndex === index && (
                  <Box sx={{ textAlign: 'center', marginTop: 2 }}>
                    {!inStep && ableToStartStep() && (
                      <Button variant="contained" color="primary" onClick={handleStartStep}>
                        Start
                      </Button>
                    )}
                    {!inStep && inIdVerificationProcess() && (
                      <Button variant="contained" color="primary" onClick={handleStartStep}>
                        Continue
                      </Button>
                    )}
                  </Box>
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Container>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
    case 'add_phone':
      return (
        <AddPhoneStep
          currentUser={currentUser}
          afterCancel={handleCancelStep}
          afterSuccess={updateFlirtAccountSetup}
          phones={phones}
        />
      );
    case 'add_cc':
      return (
        <AddCreditCardStep
          currentUser={currentUser}
          afterCancel={handleCancelStep}
          afterSuccess={updateFlirtAccountSetup}
        />
      );
    case 'verify_id':
      return (
        <IdVerificationStep
          currentUser={currentUser}
          afterCancel={handleCancelStep}
          afterSuccess={updateFlirtAccountSetup}
          idvStatus={idvStatus}
          idvDate={idvDate}
        />
      );
    case 'add_payout_info':
      return (
        <PayoutInfoStep
          afterCancel={handleCancelStep}
          afterSuccess={updateFlirtAccountSetup}
        />
      );
    case 'gender':
      return <div>Set Gender Component</div>;
    default:
      return null;
    }
  };

  return (
    <BoxFullPage>
      <Container maxWidth="lg">
        <Box sx={{ padding: 2 }} mt={inStep ? 1 : 4}>
          <Box sx={{ textAlign: 'center' }}>
            {!inStep && <h1>Set Up Your Flirt Account</h1>}
          </Box>

          {renderStepper()}

          {idvStatus === 'completed' && (
            <Box sx={{ textAlign: 'center', marginTop: 2 }}>
              Your ID is being verified.
              <br />
              <br />
              Please check back in a few minutes.
              <br />
            </Box>
          )}

          {inStep && renderCurrentStep()}

          <Box sx={{ textAlign: 'center', marginTop: 10 }}>
            <Link href="#" underline="hover">
              Why all these steps blah blah?
            </Link>
          </Box>
        </Box>
      </Container>
    </BoxFullPage>
  );
};

export default FlirtAccountSetupContainer;
